import { uiText } from '../../i18n/uiText';
import React, { useEffect, useRef, useState } from 'react';
import { loadPdfRenderer } from '../../utils/pdfRenderer';

export default function ReportPdfPreview({ url }) {
  const host = useRef(null);
  const [document, setDocument] = useState(null), [page, setPage] = useState(1);
  const [width, setWidth] = useState(600), [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(true), [error, setError] = useState(''), [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true, task;
    setDocument(null); setPage(1); setError(''); setBusy(true);
    loadPdfRenderer().then(pdf => {
      if (!active) return null;
      task = pdf.getDocument({ url, isEvalSupported: false, useSystemFonts: false, stopAtErrors: true });
      return task.promise;
    }).then(doc => { if (active && doc) setDocument(doc); })
      .catch(() => { if (active) { setError(uiText("Не удалось показать страницы PDF. Повторите просмотр или откройте файл отдельно.")); setBusy(false); } });
    return () => { active = false; task?.destroy().catch(() => {}); };
  }, [url, retry]);
  useEffect(() => {
    if (!host.current) return undefined;
    const observer = new ResizeObserver(entries => setWidth(Math.max(160, entries[0].contentRect.width - 24)));
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!document || !host.current) return undefined;
    let active = true, renderTask;
    const surface = host.current, canvas = window.document.createElement('canvas');
    surface.replaceChildren(); setBusy(true); setError('');
    document.getPage(page).then(async source => {
      if (!active) return;
      const natural = source.getViewport({ scale: 1 });
      const cssWidth = Math.min(1400, width * zoom);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(4000000 / (cssWidth * cssWidth * natural.height / natural.width)));
      const viewport = source.getViewport({ scale: cssWidth / natural.width * pixelRatio });
      canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
      canvas.style.width = `${cssWidth}px`; canvas.style.height = `${cssWidth * natural.height / natural.width}px`;
      canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', uiText("Страница {v0} из {v1}", {v0: (page), v1: (document.numPages)}));
      renderTask = source.render({ canvasContext: canvas.getContext('2d'), viewport });
      await renderTask.promise;
      if (active) { canvas.dataset.ready = 'true'; surface.replaceChildren(canvas); setBusy(false); }
    }).catch(e => { if (active && e?.name !== 'RenderingCancelledException') { setError(uiText("Не удалось отобразить страницу. Попробуйте ещё раз или откройте PDF отдельно.")); setBusy(false); } });
    return () => { active = false; renderTask?.cancel(); canvas.remove(); };
  }, [document, page, width, zoom]);
  return <div className="audit-report-pdf-viewer" aria-label={uiText("Сформированный отчёт PDF")}>
    {document && <div className="audit-report-pdf-toolbar">
      <button className="ap-button ap-button--secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{uiText("Предыдущая страница")}</button>
      <span role="status">{uiText("Страница ")}{page}{uiText(" из ")}{document.numPages}</span>
      <button className="ap-button ap-button--secondary" disabled={page >= document.numPages} onClick={() => setPage(p => p + 1)}>{uiText("Следующая страница")}</button>
      <button className="ap-button ap-button--secondary" disabled={zoom <= 1} onClick={() => setZoom(z => Math.max(1, z - .25))} aria-label={uiText("Уменьшить страницу PDF")}>−</button>
      <button className="ap-button ap-button--secondary" disabled={zoom >= 2} onClick={() => setZoom(z => Math.min(2, z + .25))} aria-label={uiText("Увеличить страницу PDF")}>+</button>
    </div>}
    {busy && <p role="status" className="audit-report-help">{uiText("Отображаем страницу…")}</p>}
    {error && <div className="audit-report-error" role="alert">{error}<button className="ap-button ap-button--secondary" onClick={() => setRetry(r => r + 1)}>{uiText("Повторить просмотр")}</button></div>}
    <div ref={host} className="audit-report-pdf-pages" aria-busy={busy}/>
  </div>;
}
